'use client'

import * as React from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { m, useAnimationControls, LazyMotion, domAnimation } from 'framer-motion'
import type { MotionStyle } from 'framer-motion'
import { tw } from 'utils/classnames'
import { IconButton } from 'primitives/buttons/icon-button'
import { HamburgerIcon } from 'primitives/icons'

const overlayVariants = {
  expanded: { opacity: 1, transition: { duration: 0.15 } },
  collapsed: { opacity: 0, transition: { duration: 0.15 } },
}

const contentVariants = {
  expanded: {
    x: [-50, 0],
    opacity: [0, 1],
    transition: { ease: 'easeOut' as const, duration: 0.2 },
  },
  collapsed: {
    x: [0, -50],
    opacity: [1, 0],
    transition: { ease: 'easeOut' as const, duration: 0.2 },
  },
}

interface HamburgerMenuProps {
  Icon?: React.ReactNode
  hasContentFullWidth?: boolean
  shouldHideOverlay?: boolean
  controlledOpen?: boolean
  onOpenChange?: (open: boolean) => void
  triggerClassName?: string
  contentClassName?: string
  style?: MotionStyle
}

export const HamburgerMenu = ({
  children,
  Icon,
  hasContentFullWidth = false,
  shouldHideOverlay = false,
  controlledOpen,
  onOpenChange,
  triggerClassName,
  contentClassName,
  style,
}: React.PropsWithChildren<HamburgerMenuProps>) => {
  const [open, setOpen] = React.useState(false)
  const isControlled = controlledOpen !== undefined
  const actualOpen = isControlled ? controlledOpen : open

  const overlayControls = useAnimationControls()
  const contentControls = useAnimationControls()

  React.useEffect(() => {
    const animate = async () => {
      if (actualOpen) {
        await overlayControls.start('expanded')
        await contentControls.start('expanded')
      }
    }

    animate()
  }, [actualOpen, overlayControls, contentControls])

  function onCloseFromContentOverlay(event: React.MouseEvent<HTMLDivElement>) {
    if (event.target === event.currentTarget) {
      handleOverlayStateChange(false)
    }
  }

  const handleOverlayStateChange = async (isOpened: boolean) => {
    if (!isOpened) {
      // Ensure content animation starts first and completes before overlay starts
      await contentControls.start('collapsed')
      await overlayControls.start('collapsed')
    }

    if (isControlled) {
      onOpenChange?.(isOpened)
    } else {
      setOpen(isOpened)
    }
  }

  return (
    <LazyMotion features={domAnimation}>
      <Dialog.Root key="hamburger-menu" open={actualOpen} onOpenChange={handleOverlayStateChange}>
        <Dialog.Trigger asChild>
          <IconButton
            aria-label="Open menu"
            variant="transparent"
            className={tw('text-service-white-white', triggerClassName)}
          >
            {Icon ? (
              Icon
            ) : (
              <HamburgerIcon className="w-[24px]" style={{ transform: 'translateZ(0)' }} />
            )}
          </IconButton>
        </Dialog.Trigger>
        <Dialog.Portal>
          <m.div
            animate={overlayControls}
            variants={overlayVariants}
            key="overlay"
            className="relative"
          >
            {/* bg colour is hard coded so it's the same value on dark and light mode */}
            {shouldHideOverlay ? null : (
              <Dialog.Overlay
                forceMount
                className="fixed inset-0 z-30 bg-[#272B2F] bg-opacity-80 backdrop-blur-[6px]"
              />
            )}
          </m.div>

          <Dialog.Content key="content" asChild forceMount onClick={onCloseFromContentOverlay}>
            <m.div
              animate={contentControls}
              variants={contentVariants}
              style={style}
              className={tw(
                `bg-service-white fixed inset-0 z-40 flex h-[100dvh] ${
                  hasContentFullWidth ? 'w-full' : 'w-[300px]'
                } outline-none`,
                contentClassName,
              )}
            >
              {children}
            </m.div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </LazyMotion>
  )
}
